import { entities, EventPushResponse } from "sbtech-sports-data-api-client";
import { SportDataGatewayQueryInfo, SportDataGatewayStreamInstance } from "sb-resp-lib";
import { observable } from "mobx";

export const initialSportsDataFeed: EventPushResponse = {
    data: {
        add: {
            events: [],
            markets: []
        },
        change: {
            events: [],
            markets: []
        },
        remove: {
            events: [],
            markets: []
        }
    },
    warnings: []
};

export const failureSportsDataFeed: EventPushResponse = {
    data: {
        add: {
            events: [],
            markets: []
        },
        change: {
            events: [],
            markets: []
        },
        remove: {
            events: [],
            markets: []
        }
    },
    warnings: []
};

/*
    The class has 'istanbul ignore' flag , because of testing typescript interface with Jest.
    It`s known issue : (https://github.com/kulshekhar/ts-jest/issues/378)
    The class is fully tested, but propery "sportsDataChangeFeed" implement interface "ChangeFeedUpdate" which is not
    testable with Jest - there is no types like in typescript
 */

/* istanbul ignore next */
export class SportDataGateWayRetriever {
    uniqueId: string;
    localeCodeQueryFormat: string;
    eventIds:number[] = [];
    @observable sportsDataChangeFeed: EventPushResponse | undefined;

    @observable shouldRemoveEvents: boolean;

    @observable healthCheck: boolean;

    constructor(
        locale: string = "en",
        uniqueId: string = "superbanner-sportsdata-retriever"
    ) {
        this.localeCodeQueryFormat = locale;
        this.sportsDataChangeFeed = initialSportsDataFeed;
        this.healthCheck = true;
        this.uniqueId = uniqueId;
        this.shouldRemoveEvents = false;

        this.messageHandler = this.messageHandler.bind(this);
    }

    activate(eventIds: number[]): void {
        if (eventIds.length === 0) {
            return;
        }
        this.eventIds = eventIds;
        let eventsQuery = this.queryBuilder(this.eventIds);
        let queryInfo = new SportDataGatewayQueryInfo(eventsQuery, this.messageHandler);
        queryInfo.initialData = "true";
        queryInfo.locale = this.localeCodeQueryFormat;
        queryInfo.onErrorHandler = this.setFailureSportsDataChangeFeed.bind(this, this.eventIds);

        SportDataGatewayStreamInstance.subscribeForEvents({
            id: this.uniqueId,
            entityType: entities.EntityType.Event,
            queryInfo
        });
    }

    setFailureSportsDataChangeFeed(eventIds: number[]): void {
        this.shouldRemoveEvents = true;
    }

    deactivate(): void {
        SportDataGatewayStreamInstance.unsubscribe(entities.EntityType.Event, this.uniqueId);
    }

    messageHandler(data: EventPushResponse): void {
        this.shouldRemoveEvents = false;
        let response:EventPushResponse = this.filterResponse(data);
        if (this.shouldProcessUpdate(response)) {
            // observed by Data Facade for changes
            this.sportsDataChangeFeed = response;
        }
    }

    shouldProcessUpdate(res: EventPushResponse):boolean {
        return !(res.data.add.events.length === 0
            && res.data.add.markets.length === 0
            && res.data.change.events.length === 0
            && res.data.change.markets.length === 0
            && res.data.remove.events.length === 0
            && res.data.remove.markets.length === 0);
    }

    filterResponse(data: EventPushResponse):EventPushResponse {
        let filteredResponse: EventPushResponse = {
            data: {
                add: {
                    events: this.filterEntityByEventId<entities.SportEvent>(data.data.add.events,
                        (event, eventId) => eventId === parseInt(event.id, 10)),
                    markets: this.filterEntityByEventId<entities.Market>(data.data.add.markets,
                        (market, eventId) => eventId === parseInt(market.eventId, 10)),
                },
                change: {
                    events: this.filterEntityByEventId<entities.SportEventChange>(data.data.change.events,
                        (event, eventId) => eventId === parseInt(event.id, 10)),
                    markets: this.filterEntityByEventId<entities.MarketChange>(data.data.change.markets,
                        (market, eventId) => eventId === parseInt(market.eventId, 10)),
                },
                remove: {
                    events: this.filterEntityByEventId<string>(data.data.remove.events,
                        (event, eventId) => eventId === parseInt(event, 10)),
                    markets: data.data.remove.markets
                }
            },
            warnings: []
        };
        return filteredResponse;
    }

    private filterEntityByEventId<T>(entity: T[], filterCallback: (entityEventId: T, eventId: number) => boolean):T[] {
        let ref = this;
        return entity.filter(e => ref.eventIds.some(eventId => filterCallback.call(ref, e, eventId)));
    }

    queryBuilder(eventIds: number[]): string {
        /* istanbul ignore next */
        let itemIdsStr: string = `${eventIds.map(
            function (id: number, index: number): string {
                let string: string = `id eq '${id}'`;
                if (index === 0) {
                    return string;
                } else {
                    return ` or ${string}`;
                }
            }
        ).join("")}`;
        let query: string = `$filter=${itemIdsStr}`;
        return query;
    }
}
